// Porta JS do módulo do back-end (`src/project/disbursement/profile.js`).
// Mesmos nomes, mesmas mensagens, mesmas invariantes: o editor não pode aceitar
// um perfil que a rota vai recusar.

export const MIN_OFFSET = -12;
export const MAX_OFFSET = 36;
export const MAX_LINES = 60;
export const AT_SIGHT_PROFILE = Object.freeze([
  Object.freeze({ offsetMonths: 0, percentBps: 10000 }),
]);

export class ProfileError extends Error {
  constructor(errorCode, message) {
    super(message);
    this.name = "ProfileError";
    this.errorCode = errorCode;
    this.statusCode = 400;
  }
}

const isLine = (line) => (
  line
  && typeof line === "object"
  && Number.isSafeInteger(line.offsetMonths)
  && Number.isSafeInteger(line.percentBps)
  && line.percentBps > 0
);

export const validateProfile = (profile) => {
  if (!Array.isArray(profile) || profile.length === 0) {
    throw new ProfileError("profile_empty", "A forma de desembolso precisa de pelo menos uma linha");
  }
  if (profile.length > MAX_LINES) {
    throw new ProfileError("profile_too_long", `A forma de desembolso aceita no máximo ${MAX_LINES} linhas`);
  }
  if (!profile.every(isLine)) {
    throw new ProfileError("profile_line_invalid", "Cada linha precisa de defasagem inteira e percentual positivo");
  }
  const offsets = new Set();
  let sum = 0;
  for (const line of profile) {
    if (line.offsetMonths < MIN_OFFSET || line.offsetMonths > MAX_OFFSET) {
      throw new ProfileError("profile_offset_range", `A defasagem deve ficar entre ${MIN_OFFSET} e ${MAX_OFFSET} meses`);
    }
    if (offsets.has(line.offsetMonths)) {
      throw new ProfileError("profile_offset_duplicate", "Cada defasagem só pode aparecer uma vez");
    }
    offsets.add(line.offsetMonths);
    sum += line.percentBps;
  }
  if (sum !== 10000) {
    throw new ProfileError("profile_sum", "Os percentuais precisam somar exatamente 100%");
  }
  return profile
    .map(({ offsetMonths, percentBps }) => ({ offsetMonths, percentBps }))
    .sort((left, right) => left.offsetMonths - right.offsetMonths);
};

const assertInstallments = (installments, startOffset) => {
  if (!Number.isSafeInteger(installments) || installments < 1) {
    throw new ProfileError("profile_line_invalid", "Número de parcelas inválido");
  }
  if (!Number.isSafeInteger(startOffset)) {
    throw new ProfileError("profile_line_invalid", "Defasagem inicial inválida");
  }
  if (startOffset < MIN_OFFSET || startOffset + installments - 1 > MAX_OFFSET) {
    throw new ProfileError("profile_offset_range", `As parcelas precisam caber entre ${MIN_OFFSET} e ${MAX_OFFSET} meses`);
  }
};

const splitBps = (count) => {
  const base = Math.floor(10000 / count);
  const remainder = 10000 - base * count;
  return Array.from({ length: count }, (_, index) => base + (index < remainder ? 1 : 0));
};

export const equalInstallments = ({ installments, startOffset = 0 } = {}) => {
  assertInstallments(installments, startOffset);
  return validateProfile(
    splitBps(installments).map((percentBps, index) => ({
      offsetMonths: startOffset + index,
      percentBps,
    })),
  );
};

export const downPaymentPlusInstallments = ({
  downPaymentBps,
  downPaymentOffset,
  installments,
  startOffset = 0,
} = {}) => {
  if (!Number.isSafeInteger(downPaymentBps) || downPaymentBps <= 0 || downPaymentBps >= 10000) {
    throw new ProfileError("profile_line_invalid", "A entrada precisa ficar entre 1% e 99%");
  }
  if (
    !Number.isSafeInteger(downPaymentOffset)
    || downPaymentOffset < MIN_OFFSET
    || downPaymentOffset > MAX_OFFSET
  ) {
    throw new ProfileError("profile_offset_range", `A defasagem da entrada deve ficar entre ${MIN_OFFSET} e ${MAX_OFFSET} meses`);
  }
  assertInstallments(installments, startOffset);
  const rest = 10000 - downPaymentBps;
  const base = Math.floor(rest / installments);
  const remainder = rest - base * installments;
  const lines = [{ offsetMonths: downPaymentOffset, percentBps: downPaymentBps }];
  for (let index = 0; index < installments; index += 1) {
    lines.push({
      offsetMonths: startOffset + index,
      percentBps: base + (index < remainder ? 1 : 0),
    });
  }
  return validateProfile(lines);
};

const percentText = (bps) => {
  const value = bps / 100;
  return `${Number.isInteger(value) ? value : value.toFixed(2).replace(".", ",")}%`;
};

const offsetText = (offset) => {
  if (offset === 0) return "na execução";
  const abs = Math.abs(offset);
  const unit = abs === 1 ? "mês" : "meses";
  return offset < 0 ? `${abs} ${unit} antes` : `${abs} ${unit} depois`;
};

export const describeProfile = (profile) => {
  const lines = validateProfile(profile);
  if (lines.length === 1 && lines[0].offsetMonths === 0) return "à vista";
  const runStart = (() => {
    let start = lines.length - 1;
    while (start > 0) {
      const previous = lines[start - 1];
      const current = lines[start];
      if (
        current.offsetMonths !== previous.offsetMonths + 1
        || Math.abs(current.percentBps - previous.percentBps) > 1
      ) break;
      start -= 1;
    }
    return start;
  })();
  const head = lines.slice(0, runStart);
  const run = lines.slice(runStart);
  const parts = head.map((line) => `${percentText(line.percentBps)} ${offsetText(line.offsetMonths)}`);
  if (run.length >= 2) {
    const runBps = run.reduce((sum, line) => sum + line.percentBps, 0);
    const from = run[0].offsetMonths === 0
      ? "a partir da execução"
      : `a partir de ${offsetText(run[0].offsetMonths)}`;
    parts.push(`${percentText(runBps)} em ${run.length} parcelas ${from}`);
  } else {
    parts.push(...run.map((line) => `${percentText(line.percentBps)} ${offsetText(line.offsetMonths)}`));
  }
  return parts.join(" · ");
};
